import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Button as MuiButton,
  Paper,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Spacer,
} from "_components/common";
import { generateCodeForEmail } from "_services/verifyService";
import * as types from "_constants";
import logo from 'img/brands/OTS-logo.png';
import { signOut } from "_redux/actions/appActions";
import { parseJwt, checkJwtExpired } from "_utils/helpers";
import { FormRedirect } from "./FormRedirect";

const Alert = styled(MuiAlert)(spacing);
const Button = styled(MuiButton)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)}px;
  }
`;

function GenCode() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoaded, setIsLoaded] = useState(false);
  const { organizationSlug } = useParams();
  const search = useLocation().search;
  const [formRedirect, setFormRedirect] = useState(null);

  useEffect(() => {
    const redirect_url = new URLSearchParams(search).get('redirect_url');
    const redirect_app_url = new URLSearchParams(search).get('redirect_app_url');
    const redirect_err_url = new URLSearchParams(search).get('redirect_err_url');
    const logout = new URLSearchParams(search).get('logout');
    if (redirect_url === undefined || redirect_url === null) {
      // redirect to overview page if no url is found
      history.push(`/overview/${organizationSlug}`);
    } else {
      // in onstage if the session is reset, then force logout & let user login again
      if (logout && logout === 'true') {
        localStorage.removeItem('access_token');
        setIsLoaded(true);
        return;
      }

      const userToken = localStorage.getItem('access_token');
      if (userToken) {
        console.log('userToken found: ', userToken);
        const user = parseJwt(userToken);
        console.log('parsed token: ', user);
        if (!checkJwtExpired(user.exp)) {
          console.log('JWT expired: ', user.exp);
          dispatch(signOut());
          history.push(`/auth/${organizationSlug}/verify?redirect_url=/overview/${organizationSlug}`);
        } else {
          if (redirect_url.indexOf('http') > -1) {
            //let url = `${redirect_url}?token=${userToken}&redirect_app_url=${redirect_app_url}`;
            let formData = {
              redirect_url: redirect_url,
              token: userToken,
              redirect_app_url: redirect_app_url,
            }
            if (redirect_err_url) {
              //url = `${url}&redirect_err_url=${redirect_err_url}`;
              formData = {
                ...formData,
                redirect_err_url: redirect_err_url
              };
            }
            // if (window.opener) {
            //   window.opener.location.href = url;
            //   window.close();
            // } else {
              //window.location.href = url;
            //}
            setFormRedirect(formData);
          } else {
            history.push(redirect_url);
          }
        }
      } else {
        setIsLoaded(true);
      }
    }
  }, [search]);

  if (formRedirect) {
    return <FormRedirect {...formRedirect} />
  }

  if (!isLoaded) {
    return <></>;
  }

  return (
    <Wrapper>
      <Helmet title="Generate Code | OnTheStage" />
      <Spacer my={3} />
      <div style={{width: '100%', textAlign: 'center'}}>
        <img alt="OTS LOGO" src={logo} style={{width: '256px'}} />
      </div>

      <Typography component="h2" variant="body1" align="center">
        Enter your email to receive a confirmation code:
      </Typography>

      <Formik
        initialValues={{
          email: "",
          submit: false
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Must be a valid email")
            .max(512)
            .required("Email is required"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            const data = await generateCodeForEmail(values.email, organizationSlug);
            dispatch({
              type: types.GENERATECODE_SUCCESS,
              payload: {
                email: values.email,
                sessionId: data.sessionId,
                expiry: data.expiry
              }
            });
            const redirect_url = new URLSearchParams(search).get('redirect_url');
            const redirect_app_url = new URLSearchParams(search).get('redirect_app_url');
            const redirect_err_url = new URLSearchParams(search).get('redirect_err_url');
            if (redirect_url) {
              let url = redirect_app_url !== null ? `/auth/${organizationSlug}/confirm?redirect_url=${redirect_url}&redirect_app_url=${redirect_app_url}` : `/auth/${organizationSlug}/confirm?redirect_url=${redirect_url}`;
              if (redirect_err_url) {
                url = `${url}&redirect_err_url=${redirect_err_url}`;
              }
              history.push(url);
            }
          } catch (error) {
            const message = error.response?.data || error.message || "Something went wrong";
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert mt={2} mb={1} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              type="email"
              autoFocus
              name="email"
              variant="outlined"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              mb={2}
              mt={2}
            >
              Send Code
            </Button>

          </form>
        )}
      </Formik>
    </Wrapper>);
}

export default GenCode;